import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ExternalLink, Link as LinkIcon, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const Fixtures = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', url: '', category: '' });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await api.get('/links');
      setLinks(response.data);
    } catch (error) {
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.url) {
      toast.error('Name and URL are required');
      return;
    }
    try {
      const response = await api.post('/links', formData);
      setLinks([response.data, ...links]);
      setFormData({ name: '', url: '', category: '' });
      setShowForm(false);
      toast.success('Link added successfully');
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await api.delete(`/links/${linkId}`);
      setLinks(links.filter(l => l.id !== linkId));
      toast.success('Link deleted');
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const filteredLinks = links
    .filter(l =>
      l.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (l.category && l.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      l.url.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const categories = ['Fixtures', 'League Tables', 'News', 'Stats', 'Scouting', 'Other'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 data-testid="links-page-title" className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                Links
              </h1>
              <p className="text-lg text-zinc-300">{filteredLinks.length} saved links</p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              data-testid="add-link-button"
              className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
            >
              <Plus className="w-5 h-5" strokeWidth={2} />
              Add Link
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Add Link Form */}
        {showForm && (
          <div className="glass-card p-6 mb-6">
            <h2 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Add New Link</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Link name *"
                data-testid="link-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
              />
              <input
                type="url"
                placeholder="URL (https://...) *"
                data-testid="link-url-input"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
              />
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  type="submit"
                  data-testid="save-link-button"
                  className="flex-1 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-4 py-3 transition-all"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setFormData({ name: '', url: '', category: '' }); }}
                  className="px-4 py-3 bg-white/10 text-white hover:bg-white/20 rounded-sm transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="glass-card p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search links..."
              data-testid="search-links-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
            />
          </div>
        </div>

        {/* Links Grid */}
        {filteredLinks.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <LinkIcon className="w-12 h-12 text-zinc-600 mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-zinc-500 text-lg">No links saved yet. Add your first link to get started!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLinks.map((link) => (
              <div
                key={link.id}
                data-testid={`link-card-${link.id}`}
                className="glass-card p-5 group hover:border-white/20 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white truncate">{link.name}</h3>
                    {link.category && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-white/10 text-white text-xs font-medium rounded">
                        {link.category}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(link.id)}
                    data-testid={`delete-link-${link.id}`}
                    className="p-1.5 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors truncate"
                >
                  <ExternalLink className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{link.url}</span>
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Fixtures;
